
import { Box, Typography } from "@mui/material";
import { useState } from "react";

import {
  HOPE_SANS,
  HOPE_SERIF,
} from "@/shared/branding/thehopeonebrand";

import { stripImpactHtml } from "./ImpactQuillField";

/* -------------------------------------------------------------------------- */
/* Theme                                                                      */
/* -------------------------------------------------------------------------- */

const c = {
  forestDeep: "#102C24",
  forest: "#1D4437",
  muted: "#5A6B63",
  mist: "#F3F6F3",
  border: "rgba(16,44,36,.10)",
};

/* -------------------------------------------------------------------------- */
/* Shared media layout                                                        */
/* -------------------------------------------------------------------------- */

const mediaContainerSx = {
  width: "100%",
  maxWidth: 680,
  minWidth: 0,
  mx: "auto",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  overflow: "hidden",
  borderRadius: "18px",

  bgcolor: c.mist,
  border: `1px solid ${c.border}`,
} as const;

/* -------------------------------------------------------------------------- */
/* Image                                                                      */
/* -------------------------------------------------------------------------- */

function Photo({
  src,
  alt,
  index,
}: {
  src: string;
  alt: string;
  index: number;
}) {
  // Associate the error with the URL, so a new source can be attempted.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const failed = failedSrc === src;
  const isExternal = /^https?:\/\//i.test(src);
  const imageName = (() => {
    try {
      const path = new URL(src, window.location.href).pathname;
      return decodeURIComponent(path.split("/").pop() || src);
    } catch {
      return src.split("/").pop() || src;
    }
  })();

  if (failed) {
    return (
      <Box
        role="alert"
        sx={{
          ...mediaContainerSx,
          minHeight: 150,
          px: 2,
          py: 3,
          flexDirection: "column",
          gap: 1,
          border: "1px solid #D66B6B",
          bgcolor: "#FFF5F5",
        }}
      >
        <Typography sx={{ fontFamily: HOPE_SANS, fontSize: "0.95rem", fontWeight: 700, color: "#B42318", textAlign: "center" }}>
          이미지 {index + 1}을(를) 불러올 수 없습니다.
        </Typography>
        <Typography sx={{ fontFamily: HOPE_SANS, fontSize: "0.8rem", color: "#7A271A", textAlign: "center", overflowWrap: "anywhere" }}>
          파일: {imageName}
        </Typography>
        <Typography sx={{ fontFamily: HOPE_SANS, fontSize: "0.75rem", color: "#7A271A", textAlign: "center" }}>
          이미지 출처: {isExternal ? "외부 URL" : "로컬 또는 앱 내부 경로"}
        </Typography>
        <Typography sx={{ fontFamily: HOPE_SANS, fontSize: "0.7rem", color: "#7A271A", textAlign: "center", overflowWrap: "anywhere" }}>
          경로: {src}
        </Typography>
        <Typography sx={{ fontFamily: HOPE_SANS, fontSize: "0.72rem", color: "#7A271A", textAlign: "center" }}>
          파일 경로, 서버 응답 및 이미지 형식을 확인해 주세요.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={mediaContainerSx}>
      <Box
        component="img"
        src={src}
        alt={alt}
        loading="eager"
        decoding="async"
        draggable={false}
        onError={() => {
          console.error("[ImpactStoryGallery] Image failed to load", {
            index: index + 1,
            imageName,
            src,
            sourceType: isExternal ? "external" : "local-or-internal",
          });
          setFailedSrc(src);
        }}
        sx={{
          display: "block",
          width: "auto",
          height: "auto",
          maxWidth: "100%",
          maxHeight: {
            xs: "min(58dvh, 440px)",
            sm: "min(65dvh, 560px)",
          },
          objectFit: "contain",
          objectPosition: "center center",
          m: 0,
          p: 0,
          border: "none",
          borderRadius: 0,
          boxShadow: "none",
          userSelect: "none",
          WebkitUserDrag: "none",
        }}
      />
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/* Gallery                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Displays all supplied images without cropping.
 *
 * Single image:
 * - centered
 * - natural aspect ratio
 * - limited maximum width and height
 *
 * Multiple images:
 * - vertically stacked
 * - each image fully visible
 * - no cropped mosaic tiles
 *
 * The gallery does not force images to fill
 * a fixed-size frame.
 */
export function ImpactStoryGallery({
  urls,
}: {
  urls: string[];
}) {
  const photos = urls.filter(
    (url): url is string =>
      typeof url === "string" &&
      url.trim().length > 0
  );

  if (photos.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,

        display: "flex",
        flexDirection: "column",

        alignItems: "center",

        gap: {
          xs: 1.5,
          sm: 2,
        },
      }}
    >
      {photos.map((src, index) => (
        <Photo
          key={`${src}-${index}`}
          src={src}
          index={index}
          alt={
            photos.length === 1
              ? "현장 사진"
              : `현장 사진 ${index + 1}`
          }
        />
      ))}
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/* Video                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Direct video player.
 *
 * The video is never cropped.
 *
 * The player uses the video's intrinsic dimensions
 * when metadata is available and applies a maximum
 * width and height for a compact presentation.
 */
export function ImpactStoryVideo({
  src,
  caption,
}: {
  src: string;
  caption?: string;
}) {
  const [videoRatio, setVideoRatio] =
    useState<number | null>(null);

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,

        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",

          /*
           * Portrait videos are narrower than
           * landscape videos.
           */
          maxWidth:
            videoRatio !== null && videoRatio < 1
              ? 320
              : 680,

          mx: "auto",

          overflow: "hidden",
          borderRadius: "18px",

          bgcolor: "#000000",

          border: `1px solid ${c.border}`,

          boxShadow:
            "0 12px 32px rgba(16,44,36,.10)",
        }}
      >
        <Box
          component="video"
          src={src}
          controls
          playsInline
          preload="metadata"
          onLoadedMetadata={(event) => {
            const video = event.currentTarget;

            if (
              video.videoWidth > 0 &&
              video.videoHeight > 0
            ) {
              setVideoRatio(
                video.videoWidth /
                  video.videoHeight
              );
            }
          }}
          sx={{
            display: "block",

            width: "100%",
            height: "auto",

            /*
             * Never crop or stretch the video.
             */
            maxHeight: {
              xs: "min(60dvh, 460px)",
              sm: "min(70dvh, 600px)",
            },

            aspectRatio:
              videoRatio !== null
                ? `${videoRatio}`
                : "auto",

            objectFit: "contain",
            objectPosition: "center",

            bgcolor: "#000000",

            border: "none",
            borderRadius: 0,
          }}
        />
      </Box>

      {caption ? (
        <Typography
          sx={{
            mt: 1.5,

            width: "100%",
            maxWidth: 680,

            fontFamily: HOPE_SERIF,

            fontSize: {
              xs: "1rem",
              sm: "1.12rem",
            },

            lineHeight: 1.7,

            color: c.forestDeep,

            textAlign: "center",

            wordBreak: "keep-all",
            overflowWrap: "break-word",
          }}
        >
          {caption}
        </Typography>
      ) : null}
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/* Text                                                                       */
/* -------------------------------------------------------------------------- */

export function ImpactStoryText({
  title,
  body,
}: {
  title?: string;
  body?: string;
}) {
  const html = body?.trim() ?? "";
  const plain = stripImpactHtml(html);

  if (!title?.trim() && !plain) {
    return null;
  }

  const isHtml =
    /<\/?[a-z][\s\S]*>/i.test(html);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "40rem",

        mx: "auto",

        px: {
          xs: 0.15,
          sm: 0.25,
        },

        py: 0,
      }}
    >
      {title?.trim() ? (
        <Typography
          sx={{
            fontFamily: HOPE_SERIF,

            fontWeight: 650,

            fontSize: {
              xs: "1.65rem",
              sm: "2.05rem",
            },

            lineHeight: 1.3,

            letterSpacing: "-0.03em",

            color: c.forestDeep,

            mb: plain ? 1.25 : 0,

            wordBreak: "keep-all",
            overflowWrap: "break-word",
          }}
        >
          {title.trim()}
        </Typography>
      ) : null}

      {plain && isHtml ? (
        <Box
          dangerouslySetInnerHTML={{
            __html: html,
          }}
          sx={{
            fontFamily: HOPE_SERIF,

            fontWeight: 400,

            fontSize: {
              xs: "1.08rem",
              sm: "1.22rem",
            },

            lineHeight: 1.8,

            color: c.forest,

            overflowWrap: "break-word",

            "& p": {
              m: 0,
              mb: 1.25,
            },

            "& p:last-child": {
              mb: 0,
            },

            "& h1, & h2, & h3": {
              fontFamily: HOPE_SERIF,
              fontWeight: 700,
              color: c.forestDeep,
              mt: 0,
              mb: 1,
              lineHeight: 1.3,
            },

            "& ul, & ol": {
              pl: 2.75,
              mb: 1.25,
              mt: 0,
            },

            "& a": {
              color: c.forestDeep,
              fontWeight: 600,
              overflowWrap: "anywhere",
            },

            "& blockquote": {
              m: 0,
              mb: 1.25,
              pl: 1.75,
              borderLeft:
                "3px solid rgba(16,44,36,.18)",
              fontStyle: "italic",
            },

            /*
             * Media embedded inside rich text
             * should also preserve its proportions.
             */
            "& img": {
              display: "block",
              width: "auto",
              height: "auto",
              maxWidth: "100%",
              maxHeight: "min(60dvh, 480px)",
              objectFit: "contain",
              mx: "auto",
              my: 1.5,
              borderRadius: "12px",
            },

            "& video": {
              display: "block",
              width: "100%",
              height: "auto",
              maxWidth: 680,
              maxHeight: "min(65dvh, 560px)",
              objectFit: "contain",
              mx: "auto",
              my: 1.5,
              borderRadius: "12px",
            },

            "& iframe": {
              display: "block",
              width: "100%",
              maxWidth: 680,
              maxHeight: "min(65dvh, 560px)",
              mx: "auto",
              my: 1.5,
              border: 0,
              borderRadius: "12px",
            },
          }}
        />
      ) : plain ? (
        <Typography
          sx={{
            fontFamily: HOPE_SERIF,

            fontWeight: 400,

            fontSize: {
              xs: "1.08rem",
              sm: "1.22rem",
            },

            lineHeight: 1.8,

            color: c.forest,

            whiteSpace: "pre-wrap",

            wordBreak: "keep-all",
            overflowWrap: "break-word",
          }}
        >
          {html}
        </Typography>
      ) : null}
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/* Shared typography                                                          */
/* -------------------------------------------------------------------------- */

export const storyCaptionSx = {
  fontFamily: HOPE_SANS,

  fontSize: {
    xs: "0.95rem",
    sm: "1.05rem",
  },

  lineHeight: 1.7,

  color: c.muted,

  wordBreak: "keep-all",
  overflowWrap: "break-word",
} as const;

export const storyTitleSx = {
  fontFamily: HOPE_SERIF,

  fontWeight: 700,

  fontSize: {
    xs: "1.65rem",
    sm: "2.1rem",
  },

  color: c.forestDeep,

  lineHeight: 1.3,

  letterSpacing: "-0.03em",

  wordBreak: "keep-all",
  overflowWrap: "break-word",
} as const;